import React, { useState, useEffect } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { approveTransfer, rejectTransfer, completeTransfer } from '../services/transferService';
import './TransferManager.css';

function TransferManager({ user }) {
  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'transfers'), (snap) => {
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      list.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
      setTransfers(list);
      setLoading(false);
    }, (err) => {
      console.error('Error loading transfers:', err);
      setLoading(false);
    });
    return () => unsub();
  }, []);

  const handleApprove = async (transfer) => {
    setBusyId(transfer.id);
    try {
      await approveTransfer(transfer.id, user);
    } catch (err) {
      console.error('Error approving transfer:', err);
      alert('Something went wrong. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const openReject = (transfer) => {
    setRejectingId(transfer.id);
    setRejectReason('');
  };

  const submitReject = async (transfer) => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejecting this transfer.');
      return;
    }
    setBusyId(transfer.id);
    try {
      await rejectTransfer(transfer.id, user, rejectReason);
      setRejectingId(null);
    } catch (err) {
      console.error('Error rejecting transfer:', err);
      alert('Something went wrong. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const handleComplete = async (transfer) => {
    if (!window.confirm(`Confirm ${transfer.equipmentSerial} has physically arrived at ${transfer.toOutletName}?`)) {
      return;
    }
    setBusyId(transfer.id);
    try {
      await completeTransfer(transfer, user);
    } catch (err) {
      console.error('Error completing transfer:', err);
      alert('Something went wrong. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const formatDate = (ts) => {
    if (!ts) return 'Just now';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleString('en-KE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const counts = {
    pending:   transfers.filter(t => t.status === 'pending').length,
    confirmed: transfers.filter(t => t.status === 'confirmed').length,
    rejected:  transfers.filter(t => t.status === 'rejected').length,
    completed: transfers.filter(t => t.status === 'completed').length
  };

  const list = transfers.filter(t => t.status === filter);

  return (
    <div className="transfer-manager">
      <div className="tm-header">
        <h2>🔄 Equipment Transfers</h2>
        {counts.pending > 0 && <span className="tm-badge">{counts.pending} awaiting review</span>}
      </div>

      <div className="tm-filters">
        <button className={`tm-pill ${filter === 'pending' ? 'active' : ''}`} onClick={() => setFilter('pending')}>
          ⏳ Pending ({counts.pending})
        </button>
        <button className={`tm-pill ${filter === 'confirmed' ? 'active' : ''}`} onClick={() => setFilter('confirmed')}>
          ✅ Approved ({counts.confirmed})
        </button>
        <button className={`tm-pill ${filter === 'completed' ? 'active' : ''}`} onClick={() => setFilter('completed')}>
          📦 Completed ({counts.completed})
        </button>
        <button className={`tm-pill ${filter === 'rejected' ? 'active' : ''}`} onClick={() => setFilter('rejected')}>
          🚫 Rejected ({counts.rejected})
        </button>
      </div>

      {loading ? (
        <div className="tm-empty">Loading transfers...</div>
      ) : list.length === 0 ? (
        <div className="tm-empty">
          <div className="tm-empty-icon">📭</div>
          <p>No {filter} transfers</p>
        </div>
      ) : (
        <div className="tm-list">
          {list.map(t => (
            <div key={t.id} className="tm-card">
              <div className="tm-card-top">
                <div className="tm-equipment">
                  {t.equipmentType === 'keg_pump' ? '☕' : '🍺'} {t.equipmentSerial}
                </div>
                <div className="tm-date">{formatDate(t.createdAt)}</div>
              </div>

              <div className="tm-route">
                <span className="tm-outlet">🏪 {t.fromOutletName}</span>
                <span className="tm-arrow">→</span>
                <span className="tm-outlet">🏪 {t.toOutletName}</span>
              </div>

              <div className="tm-reason">"{t.reason}"</div>

              <div className="tm-meta">
                Requested by {t.initiatedByName} ({t.initiatedByRole})
              </div>

              {t.status === 'rejected' && t.rejectionReason && (
                <div className="tm-rejection">🚫 Rejected: {t.rejectionReason}</div>
              )}

              {t.status === 'completed' && (
                <div className="tm-completed-note">
                  📦 Marked received by {t.completedByName} on {formatDate(t.completedAt)}
                </div>
              )}

              {t.status === 'pending' && (
                rejectingId === t.id ? (
                  <div className="tm-reject-form">
                    <textarea
                      className="tm-reject-textarea"
                      placeholder="Reason for rejecting..."
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      rows={2}
                    />
                    <div className="tm-actions">
                      <button className="tm-btn tm-cancel" onClick={() => setRejectingId(null)} disabled={busyId === t.id}>
                        Cancel
                      </button>
                      <button className="tm-btn tm-reject" onClick={() => submitReject(t)} disabled={busyId === t.id}>
                        {busyId === t.id ? '...' : 'Confirm Reject'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="tm-actions">
                    <button className="tm-btn tm-approve" onClick={() => handleApprove(t)} disabled={busyId === t.id}>
                      {busyId === t.id ? '...' : '✅ Approve'}
                    </button>
                    <button className="tm-btn tm-reject-outline" onClick={() => openReject(t)} disabled={busyId === t.id}>
                      🚫 Reject
                    </button>
                  </div>
                )
              )}

              {t.status === 'confirmed' && (
                <div className="tm-actions">
                  <button className="tm-btn tm-receive" onClick={() => handleComplete(t)} disabled={busyId === t.id}>
                    {busyId === t.id ? '...' : '📦 Mark Received at Destination'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default TransferManager;
